import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Box,
  Text,
  Avatar,
  Badge,
  IconButton,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { FiEye } from 'react-icons/fi';
import { PiStudentFill } from 'react-icons/pi';
import dayjs from 'dayjs';
import { ACTION_LABELS, ACTION_COLORS, ENTITY_TYPE_LABELS } from 'api/administrador/auditoria';

function getInitials(firstName, lastName) {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return `${first}${last}`;
}

function getAvatarColor(initials) {
  const colors = ['#574feb', '#E9B824', '#38A169', '#3182CE', '#DD6B20', '#D53F8C'];
  const charSum = (initials.charCodeAt(0) || 0) + (initials.charCodeAt(1) || 0);
  return colors[charSum % colors.length];
}

function ActionBadge({ action }) {
  const label = ACTION_LABELS[action] || action?.split('.')[1]?.toUpperCase() || 'ACCIÓN';
  const colors = ACTION_COLORS[action] || { bg: 'gray.100', color: 'gray.600', borderColor: 'gray.300' };

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="sm"
      fontSize="xs"
      fontWeight="bold"
      bg={colors.bg}
      color={colors.color}
      border="1px solid"
      borderColor={colors.borderColor}
      textTransform="uppercase"
    >
      {label}
    </Badge>
  );
}

export default function AuditTable({ logs = [], loading = false, onViewDetail }) {
  if (loading) {
    return (
      <Flex justify="center" align="center" py={10}>
        <Spinner size="lg" color="#574feb" />
      </Flex>
    );
  }

  if (logs.length === 0) {
    return (
      <Box textAlign="center" py={10} color="gray.500">
        <Text>No se encontraron registros de auditoría</Text>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr bg="gray.50">
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>
              Fecha / Hora
            </Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>
              Usuario
            </Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>
              Acción
            </Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>
              Entidad Afectada
            </Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4}>
              Detalle Rápido
            </Th>
            <Th color="gray.600" fontSize="xs" textTransform="uppercase" py={4} textAlign="center">
              Ver
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {logs
            .filter((log) => {
              const attrs = log.attributes || log;
              const userId = attrs.user?.data?.id || attrs.user?.id;
              return !!userId;
            })
            .map((log) => {
            const attrs = log.attributes || log;
            const user = attrs.user?.data?.attributes || attrs.user;
            const userId = attrs.user?.data?.id || attrs.user?.id;
            const student = attrs.student?.data?.attributes || attrs.student;
            const initials = getInitials(user?.first_name, user?.last_name);
            const avatarColor = getAvatarColor(initials);
            const entityLabel = ENTITY_TYPE_LABELS[attrs.entity_type] || attrs.entity_type;

            return (
              <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                <Td py={4}>
                  <Text fontWeight="medium" color="gray.800">
                    {dayjs(attrs.createdAt).format('DD/MM/YYYY')}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {dayjs(attrs.createdAt).format('hh:mm A')}
                  </Text>
                </Td>

                <Td py={4}>
                  <Flex align="center" gap={3}>
                    <Avatar size="sm" name={initials || '?'} bg={avatarColor} color="white" fontWeight="bold" />
                    <Box>
                      <Text fontWeight="medium" color="gray.800">
                        {user?.first_name} {user?.last_name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        ID: {userId}
                      </Text>
                    </Box>
                  </Flex>
                </Td>

                <Td py={4}>
                  <ActionBadge action={attrs.action} />
                </Td>

                <Td py={4}>
                  <Text color="gray.800">{entityLabel}</Text>
                </Td>

                <Td py={4} maxW="300px">
                  <Text color="gray.600" noOfLines={2}>
                    {attrs.summary}
                  </Text>
                  {student && (student.full_name || student.id != null) && (
                    <Flex align="center" gap={1.5} mt={1.5}>
                      <PiStudentFill size={12} color="#574feb" />
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {student.full_name || 'Sin nombre'}
                        {student.id != null && (
                          <Text as="span" color="gray.400"> · Código: {student.id}</Text>
                        )}
                      </Text>
                    </Flex>
                  )}
                </Td>

                <Td py={4} textAlign="center">
                  <IconButton
                    aria-label="Ver detalle"
                    icon={<FiEye />}
                    size="sm"
                    bg="#574feb"
                    color="white"
                    borderRadius="md"
                    _hover={{ bg: '#4a3ed9' }}
                    onClick={() => onViewDetail(log)}
                  />
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
